import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { useToast } from "@/src/components/ui/use-toast";
import { useBookingRevertToOfferMutation } from "@/src/lib/bookingRevertToOfferMutation";
import { useEffect, useState } from "react";
import { fetch } from "@/src/lib/fetch";

type Eligibility = { canRevert: boolean; payments: number; wouldDelete: { addendums: number; reviews: number }; wouldOrphan: { documents: number } };

interface RevertBookingProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  bookingId: string;
}

export const RevertBookingModal = ({ open, setOpen, bookingId }: RevertBookingProps) => {
  const revertBookingToOffer = useBookingRevertToOfferMutation(bookingId);
  const { toast } = useToast();
  // The guard the server enforces, shown before the click rather than as a failed request after it.
  const [elig, setElig] = useState<Eligibility | null>(null);
  useEffect(() => {
    if (!open) return;
    fetch<{ data: Eligibility }>(`/api/bookings/${bookingId}/revert-eligibility`).then((r: any) => setElig(r.data)).catch(() => setElig(null));
  }, [open, bookingId]);

  const onSubmit = () => {
    const toastId = toast({
      description: "Reverting booking to offer...",
      variant: "default",
      duration: Infinity,
    }).id;

    revertBookingToOffer.mutate(
      { toastId },
      {
        onSuccess: () => {
          setOpen(false);
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        if (!val) {
          setOpen(false);
        }
      }}
    >
      <DialogContent className="max-w-[480px] max-h-[calc(100%-60px)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-semi-bold">Revert Booking to Offer</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 text-sm">
          <p>Reverting deletes this booking record and reopens the offer. It can't be undone.</p>
          {elig && !elig.canRevert && (
            <p className="rounded-md bg-red-50 p-3 text-red-800">
              Not available: {elig.payments} payment{elig.payments === 1 ? "" : "s"} already recorded. Revert is only possible before any payment is received.
            </p>
          )}
          {elig && elig.canRevert && (
            <ul className="list-disc pl-5 text-slate-600">
              <li>The payment schedule, notes, checklist, commission and bank details go with the booking.</li>
              {elig.wouldDelete.addendums > 0 && <li>{elig.wouldDelete.addendums} addendum record{elig.wouldDelete.addendums === 1 ? "" : "s"} will be deleted.</li>}
              {elig.wouldDelete.reviews > 0 && <li>The trip review will be deleted.</li>}
              {elig.wouldOrphan.documents > 0 && <li>{elig.wouldOrphan.documents} contract/addendum document{elig.wouldOrphan.documents === 1 ? "" : "s"} stay in storage but won't be shown anywhere afterwards.</li>}
            </ul>
          )}
          {!elig && <p className="text-slate-500">Checking whether this booking can be reverted…</p>}
        </div>

        <DialogFooter className="flex mt-8 w-full !justify-between">
          <Button type="button" onClick={() => setOpen(false)} variant="outline">
            Cancel
          </Button>
          <Button type="submit" variant="destructive" onClick={onSubmit} disabled={!elig || !elig.canRevert || revertBookingToOffer.isPending}>
            {elig && !elig.canRevert ? "Can't revert" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
